import Api from '../../api'
import { call, put, takeLatest } from 'redux-saga/effects'
import createRequestSaga from 'portal-modules/common/redux/create-request-saga'
import createFormAction from 'portal-modules/common/redux/create-form-action'
import getProjectName from 'portal-modules/common/utils/get-project-name'
import {
  normalizePropertyLeaseViewPreference,
} from 'portal-modules/settings/utils/property-lease-visibility'

// action types
export const REQUEST_NOTIFICATION_SETTINGS =
  'rp/settings/REQUEST_NOTIFICATION_SETTINGS'
export const RECEIVE_NOTIFICATION_SETTINGS =
  'rp/settings/RECEIVE_NOTIFICATION_SETTINGS'
export const SAVE_NOTIFICATION_SETTINGS =
  'rp/settings/SAVE_NOTIFICATION_SETTINGS'
export const SAVE_NOTIFICATION_SETTINGS_NEW =
  'rp/settings/SAVE_NOTIFICATION_SETTINGS_NEW'
export const NOTIFICATION_SETTINGS_SAVED =
  'rp/settings/NOTIFICATION_SETTINGS_SAVED'
export const REQUEST_ACCOUNT_SETTINGS = 'rp/settings/REQUEST_ACCOUNT_SETTINGS'
export const RECEIVE_ACCOUNT_SETTINGS = 'rp/settings/RECEIVE_ACCOUNT_SETTINGS'
export const SAVE_ACCOUNT_SETTINGS = 'rp/settings/SAVE_ACCOUNT_SETTINGS'
export const ACCOUNT_SETTINGS_SAVED = 'rp/settings/ACCOUNT_SETTINGS_SAVED'

//reducer
export default (state = {}, { type, payload }) => {
  switch (type) {
    case RECEIVE_NOTIFICATION_SETTINGS:
      return {
        ...state,
        notificationSettings: { ...payload },
        isSettingCalled: true,
      }
    case NOTIFICATION_SETTINGS_SAVED:
      return {
        ...state,
        notificationSettings: {
          ...state.notificationSettings,
          ...Object.entries(payload || {}).reduce((obj, [key, value]) => {
            if (key === undefined || value === undefined) {
              console.warn(`Invalid key or value in payload: ${key} = ${value}`)
              return obj // Skip invalid entries
            }
            const keys = state.notificationSettings?.[key] || {}
            return {
              ...obj,
              [key]: {
                ...keys,
                value: value === undefined ? false : value,
              },
            }
          }, {}),
        },
      }
    case RECEIVE_ACCOUNT_SETTINGS:
    case ACCOUNT_SETTINGS_SAVED:
      return { ...state, ...payload }
    default:
      return state
  }
}

// action creators

export const requestNotificationSettings = (lease_id = null) => ({
  type: REQUEST_NOTIFICATION_SETTINGS,
  lease_id,
})

export const saveNotificationSettings = createFormAction(
  SAVE_NOTIFICATION_SETTINGS,
)

export const requestAccountSettings = () => ({
  type: REQUEST_ACCOUNT_SETTINGS,
})

export const receiveAccountSettings = payload => ({
  type: RECEIVE_ACCOUNT_SETTINGS,
  payload: {
    ...payload,
    language_preference: payload.language_preference || 'en_US',
    property_lease_view_preference: normalizePropertyLeaseViewPreference(
      payload.property_lease_view_preference,
    ),
  },
})

export const saveAccountSettings = ({ payload, callback }) => ({
  type: SAVE_ACCOUNT_SETTINGS,
  payload,
  callback,
})

export const saveUserNotificationSettings = ({ payload }) => ({
  type: SAVE_NOTIFICATION_SETTINGS_NEW,
  payload,
})

// sagas
export function* fetchNotificationSettings({ lease_id }) {
  const settings = yield call(Api.getNotificationSettings, { lease_id })

  if (settings && settings.length) {
    yield put({
      type: RECEIVE_NOTIFICATION_SETTINGS,
      payload: settings.reduce(
        (obj, setting) => ({
          ...obj,
          [setting.key]:
            setting.key === 'is_block_email_invoice'
              ? { ...setting, value: !setting.value }
              : setting,
        }),
        {},
      ),
    })
  }
}

export function* watchRequestNotificationSettings() {
  yield takeLatest(
    REQUEST_NOTIFICATION_SETTINGS,
    createRequestSaga(fetchNotificationSettings, {
      key: 'settings',
      message: __('Looking up your settings'),
    }),
  )
}

export function* fetchAccountSettings() {
  const { language_preference, property_lease_view_preference } = yield call(
    Api.getAccountSettings,
  )
  yield put(
    receiveAccountSettings({
      language_preference,
      property_lease_view_preference,
    }),
  )
}

export function* watchRequestAccountSettings() {
  yield takeLatest(
    REQUEST_ACCOUNT_SETTINGS,
    createRequestSaga(fetchAccountSettings, {
      key: 'settings',
      message: __('Looking up your settings'),
    }),
  )
}

export function* postNotificationSettings({ payload, lease_id }) {
  const filteredPayload = Object.entries(payload)
    ?.map(([key, value]) => ({
      key,
      value,
    }))
    ?.filter(({ key }) => key !== 'terms_and_conditions')
    ?.reduce((acc, cur) => ({ ...acc, [cur.key]: cur.value }), {})

  const updatedPayload = Object.entries(filteredPayload)?.map(
    ([key, value]) => ({
      key,
      value,
    }),
  )

  yield call(Api.saveNotificationSettings, { ...updatedPayload, lease_id })
  yield put({ type: NOTIFICATION_SETTINGS_SAVED, payload: filteredPayload })
}

export function* watchSaveNotificationSettings() {
  yield takeLatest(
    SAVE_NOTIFICATION_SETTINGS,
    createRequestSaga(postNotificationSettings, {
      key: 'settings',
      message: __('Saving your settings'),
      successMessage: __('Saved successfully'),
      write: true,
    }),
  )
}

export function* postAccountSettings({ callback, payload }) {
  yield call(Api.saveAccountSettings, payload)
  yield put({ type: ACCOUNT_SETTINGS_SAVED, payload })
  typeof callback === 'function' && callback()
}

export function* watchSaveAccountSettings() {
  yield takeLatest(
    SAVE_ACCOUNT_SETTINGS,
    createRequestSaga(postAccountSettings, {
      key: 'settings',
      message: __('Saving your settings'),
      successMessage: __('Saved successfully'),
      write: true,
    }),
  )
}

export function* watchSaveUserNotificationSettings() {
  yield takeLatest(
    SAVE_NOTIFICATION_SETTINGS_NEW,
    createRequestSaga(postNotificationSettings, {
      key: 'settings',
      message: __('Saving your settings'),
      successMessage: __('Saved successfully'),
      write: true,
    }),
  )
}

let accountSettingSagas = [
  watchRequestNotificationSettings,
  watchSaveAccountSettings,
  watchSaveNotificationSettings,
  watchSaveUserNotificationSettings,
]
if ('rh' !== getProjectName()) {
  accountSettingSagas.push(watchRequestAccountSettings)
}

export const sagas = [...accountSettingSagas]
